import { definePreset } from '@primevue/themes'
import Lara from '@primevue/themes/lara'

/**
 * Litta brand theme preset for PrimeVue.
 *
 * Brand palette:
 *   Litta Green (accent)   #03FF86
 *   Seaweed (dark teal)    #003732
 *   Oxidised Rust (mid)    #42887E
 *   Garden-Moss (darkest)  #16201B
 *   Discoloured (cream)    #F7F7F0
 *   Clear Out (white)      #FFFFFF
 *   Bin Bag (black)        #000000
 */

const brand = {
    littaGreen:   '#03FF86',
    seaweed:      '#003732',
    oxidisedRust: '#42887E',
    gardenMoss:   '#16201B',
    discoloured:  '#F7F7F0',
    clearOut:     '#FFFFFF',
    binBag:       '#000000',
} as const

export const littaPreset = definePreset(Lara, {
    primitive: {
        borderRadius: {
            none: '0',
            xs: '4px',
            sm: '8px',
            md: '12px',
            lg: '16px',
            xl: '24px',
        },
        // Teal scale derived from brand: seaweed → oxidised-rust → litta-green
        teal: {
            50:  '#f0fdf7',
            100: '#ccfce4',
            200: '#7af5b5',
            300: brand.littaGreen,
            400: brand.oxidisedRust,
            500: brand.seaweed,
            600: '#002e2a',
            700: '#002522',
            800: brand.gardenMoss,
            900: '#0d1310',
            950: '#060a08',
        },
    },
    semantic: {
        primary: {
            50:  '{teal.50}',
            100: '{teal.100}',
            200: '{teal.200}',
            300: '{teal.300}',
            400: '{teal.400}',
            500: '{teal.500}',
            600: '{teal.600}',
            700: '{teal.700}',
            800: '{teal.800}',
            900: '{teal.900}',
            950: '{teal.950}',
        },
        formField: {
            borderRadius: '{borderRadius.sm}',
            focusRing: {
                width: '0',
                style: 'none',
                color: 'transparent',
                offset: '0',
            },
        },
        colorScheme: {
            light: {
                surface: {
                    0:   brand.clearOut,
                    50:  brand.discoloured,
                    100: brand.discoloured,
                    200: '#eeede6',
                    300: '#dfddd4',
                    400: '#b0ada3',
                    500: '#8a877d',
                    600: '#6e6b62',
                    700: '#5e5c53',
                    800: '#403f38',
                    900: brand.gardenMoss,
                    950: brand.binBag,
                },
                formField: {
                    focusBorderColor: brand.littaGreen,
                    hoverBorderColor: brand.oxidisedRust,
                },
                highlight: {
                    background: '{teal.50}',
                    focusBackground: '{teal.100}',
                    color: brand.gardenMoss,
                    focusColor: brand.gardenMoss,
                },
            },
        },
        fontFamily: '"DM Sans", sans-serif',
    },
    components: {
        button: {
            root: {
                borderRadius: '9999px',
                roundedBorderRadius: '9999px',
            },
            sm: {
                paddingX: '1.0em',
                paddingY: '0.5em',
            },
            md: {
                paddingX: '1.25em',   // Pill buttons look best at 2:1 ratio
                paddingY: '0.625em',   // em values will scale with the font size
            },
            lg: {
                paddingX: '1.5em',
                paddingY: '0.75em',
            },
            colorScheme: {
                light: {
                    text: {
                        primary: {
                            color: brand.seaweed,
                            hoverBackground: 'transparent',
                            hoverColor: brand.oxidisedRust,
                            activeBackground: 'transparent',
                        },
                    },
                },
            },
        },
        tag: {
            root: {
                borderRadius: '9999px',
                roundedBorderRadius: '9999px',
            },
            colorScheme: {
                light: {
                    success: {
                        background: brand.littaGreen,
                        color: brand.seaweed,
                    },
                    info: {
                        background: '#5a9e94',
                        color: brand.clearOut,
                    },
                    warn: {
                        background: '#F6D206',
                        color: brand.seaweed,
                    },
                    danger: {
                        background: '#F04438',
                        color: brand.clearOut,
                    },
                    secondary: {
                        background: brand.discoloured,
                        color: brand.gardenMoss,
                    },
                    contrast: {
                        background: brand.seaweed,
                        color: brand.littaGreen,
                    },
                },
            },
        },
        toast: {
            root: {
                borderRadius: '{borderRadius.md}',
            },
            colorScheme: {
                light: {
                    success: {
                        background: '{teal.50}',
                        borderColor: brand.littaGreen,
                        color: brand.seaweed,
                    },
                    error: {
                        background: '#fff5f0',
                        borderColor: '#FF5500',
                        color: '#7c2d12',
                    },
                    warn: {
                        background: '#fffbeb',
                        borderColor: '#F6D206',
                        color: '#78350f',
                    },
                },
            },
        },
        togglebutton: {
            colorScheme: {
                light: {
                    root: {
                        checkedBackground: brand.seaweed,
                        checkedBorderColor: brand.seaweed,
                        checkedColor: brand.clearOut,
                    },
                },
            },
        },
        inputtext: { root: { borderRadius: '{borderRadius.sm}' } },
        select: { root: { borderRadius: '{borderRadius.sm}' } },
        textarea: { root: { borderRadius: '{borderRadius.sm}' } },
    },
})
